/**
 * @file: settings.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Per-category notification preferences. Two switches per row:
 *   - 消息中心 (inbox): whether the category shows in the inbox
 *   - 推送通知 (push):  whether the category is delivered as a push
 *
 * Server-backed via `useNotificationSettings` (read on open, written per
 * toggle). `NotificationSettingsView` is the navigation-agnostic content; the inbox
 * mounts it via a screen / desktop panel, and the Settings flow mounts it as
 * a dedicated page.
 */

import { Typography } from '@euler/app/components/typography';
import { useNotificationSettings } from '@euler/app/flows/settings/functions';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  notificationCategoryLabels,
  notificationSettingCategories,
  pushOnlyNotificationCategories,
} from '@euler/model';
import { FC, memo } from 'react';
import { ScrollView, Switch, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const NotificationSettingsView: FC = memo(() => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { settings, updateAsync } = useNotificationSettings();
  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: '#f3f3f5' }}
      contentContainerStyle={{
        paddingTop: rpx(8),
        paddingBottom: rpx(24) + insets.bottom,
      }}
    >
      <SectionHeader text="消息中心" />
      <View
        css={`
          background-color: #fff;
        `}
      >
        {notificationSettingCategories
          .filter(category => !pushOnlyNotificationCategories.has(category))
          .map(category => (
            <ToggleRow
              key={`inbox-${category}`}
              label={notificationCategoryLabels[category].title}
              hint={notificationCategoryLabels[category].subtitle}
              value={settings.inbox[category]}
              onChange={v => updateAsync('inbox', category, v)}
            />
          ))}
      </View>

      <SectionHeader text="推送通知" />
      <View
        css={`
          background-color: #fff;
        `}
      >
        {notificationSettingCategories.map(category => (
          <ToggleRow
            key={`push-${category}`}
            label={notificationCategoryLabels[category].title}
            hint={notificationCategoryLabels[category].subtitle}
            value={settings.push[category]}
            onChange={v => updateAsync('push', category, v)}
          />
        ))}
      </View>

      <Typography.Text
        color="#888"
        size={rpx(11)}
        mt={rpx(12)}
        ml={rpx(16)}
        mr={rpx(16)}
        lineHeight={rpx(18)}
      >
        关闭后该分类消息仍会留存在消息中心，但不会出现在未读提醒中。
      </Typography.Text>
    </ScrollView>
  );
});

export const NotificationSettingsScreen = wrapNavigatorScreen(
  NotificationSettingsView,
  {
    title: '通知设置',
  },
);

const SectionHeader = memo(({ text }: { text: string }) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text
      size={rpx(12)}
      color="#888"
      mt={rpx(16)}
      mb={rpx(6)}
      ml={rpx(16)}
      style={{ fontFamily: FontFamily.NotoSans.Medium }}
    >
      {text}
    </Typography.Text>
  );
});

const ToggleRow = memo(
  ({
    label,
    hint,
    value,
    onChange,
  }: {
    label: string;
    hint: string;
    value: boolean;
    onChange: (next: boolean) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          padding: ${rpx(12)}px ${rpx(16)}px;
          border-bottom-width: 0.5px;
          border-bottom-color: #eee;
        `}
      >
        <View style={{ flex: 1 }}>
          <Typography.Text
            size={rpx(14)}
            color="#222"
            style={{ fontFamily: FontFamily.NotoSans.Medium }}
          >
            {label}
          </Typography.Text>
          <Typography.Text mt={rpx(2)} size={rpx(11)} color="#999">
            {hint}
          </Typography.Text>
        </View>
        <Switch value={value} onValueChange={onChange} />
      </View>
    );
  },
);
